import {
  LockOutlined,
  MailOutlined,
  UserOutlined,
  GoogleOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { Button, Form, Input, Layout, notification, Typography, Divider, Space } from "antd";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { registerUser, googleLoginApi, AuthResponseData } from "../../../service/authApi";
import Cookies from "js-cookie";
import { secureCookieOptions } from "../../../service/utils";

const { Title, Text, Link } = Typography;

interface RegisterFormProps {
  refetch: () => void;
  onSwitchToLogin: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  refetch,
  onSwitchToLogin,
}) => {
  const [form] = Form.useForm();

  const { mutateAsync: doRegister, isLoading } = useMutation(registerUser, {
    onSuccess: (data: AuthResponseData) => {
      Cookies.set("accessToken", data.accessToken, secureCookieOptions);
      Cookies.set("refreshToken", data.refreshToken, secureCookieOptions);
      notification.success({
        message: "Konto utworzone!",
        description: "Sprawdź pocztę, aby potwierdzić adres e-mail.",
        style: { background: "#52c41a30" },
      });
      refetch();
    },
    onError: (error: any) => {
      notification.error({
        message: "Błąd rejestracji",
        description: error?.message || "Spróbuj ponownie",
        style: { background: "#ff4d4f30" },
      });
    },
  });

  const handleGoogleLogin = async () => {
    // Google Sign-In using the Google Identity Services library
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      notification.error({
        message: "Google Login nie skonfigurowany",
        description: "Brak REACT_APP_GOOGLE_CLIENT_ID w zmiennych środowiskowych",
      });
      return;
    }

    try {
      // @ts-ignore — Google Identity Services global
      const google = window.google;
      if (!google?.accounts?.id) {
        notification.error({
          message: "Google SDK nie załadowany",
          description: "Odśwież stronę i spróbuj ponownie",
        });
        return;
      }

      google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response: any) => {
          try {
            const data = await googleLoginApi(response.credential);
            Cookies.set("accessToken", data.accessToken, secureCookieOptions);
            Cookies.set("refreshToken", data.refreshToken, secureCookieOptions);
            notification.success({
              message: "Zalogowano przez Google!",
              style: { background: "#52c41a30" },
            });
            refetch();
          } catch (err: any) {
            notification.error({
              message: "Błąd logowania Google",
              description: err?.message || "Spróbuj ponownie",
              style: { background: "#ff4d4f30" },
            });
          }
        },
      });

      google.accounts.id.prompt();
    } catch {
      notification.error({
        message: "Błąd Google Login",
        description: "Spróbuj ponownie",
      });
    }
  };

  const handleSubmit = (values: any) => {
    doRegister({
      email: values.email,
      password: values.password,
      firstName: values.firstName,
      lastName: values.lastName,
    });
  };

  return (
    <Layout
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      }}
    >
      <div
        style={{
          width: 400,
          padding: 32,
          borderRadius: 12,
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Space
          direction="vertical"
          size="middle"
          style={{ width: "100%", marginBottom: 24 }}
        >
          <Title level={3} style={{ textAlign: "center", margin: 0 }}>
            Zarejestruj się
          </Title>
          <Text
            type="secondary"
            style={{ textAlign: "center", display: "block" }}
          >
            Utwórz konto w Flex Management
          </Text>
        </Space>

        <Button
          icon={<GoogleOutlined />}
          size="large"
          block
          onClick={handleGoogleLogin}
          style={{
            marginBottom: 16,
            height: 44,
            borderRadius: 8,
            fontWeight: 500,
          }}
        >
          Kontynuuj z Google
        </Button>

        <Divider plain>
          <Text type="secondary" style={{ fontSize: 12 }}>
            lub utwórz konto e-mail
          </Text>
        </Divider>

        <Form form={form} onFinish={handleSubmit} layout="vertical" size="large">
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item
              name="firstName"
              rules={[{ required: true, message: "Podaj imię" }]}
              style={{ flex: 1 }}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Imię"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
            <Form.Item
              name="lastName"
              rules={[{ required: true, message: "Podaj nazwisko" }]}
              style={{ flex: 1 }}
            >
              <Input
                placeholder="Nazwisko"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
          </div>

          <Form.Item
            name="email"
            rules={[
              { required: true, message: "Podaj adres e-mail" },
              { type: "email", message: "Nieprawidłowy adres e-mail" },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="Adres e-mail"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: "Podaj hasło" },
              { min: 8, message: "Hasło musi mieć minimum 8 znaków" },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Hasło"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Potwierdź hasło" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Hasła nie są identyczne"));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Potwierdź hasło"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 12 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              block
              style={{
                height: 44,
                borderRadius: 8,
                fontWeight: 600,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
              }}
            >
              Zarejestruj się
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: "center" }}>
          <Link onClick={onSwitchToLogin}>
            <ArrowLeftOutlined style={{ marginRight: 4 }} />
            Masz już konto? Zaloguj się
          </Link>
        </div>
      </div>
    </Layout>
  );
};
